/**
 * Guest transfer page ▸ the blocks (transferLayout.ts), each a fixed guest
 * component. Guest design (public-transfer.css, `.ptx-*`), never the staff
 * skin. With the default layout the DOM is byte for byte the page as it has
 * always been (the pinned snapshots in __tests__/guestPortal.test.tsx).
 *
 * The editor's preview frame renders through the very same components with
 * `editing` set: every block is then wrapped in a clickable frame that names
 * it and shows the selection; a hidden block is drawn faint so it can be
 * clicked back on. On the guest page nothing is wrapped.
 */
import { Fragment, createContext, useContext, type CSSProperties, type ReactNode } from 'react';
import { COMPANY_DEFAULTS, waDigits } from '@/shared/company';
import { isGuestImage } from '@/features/public-guides/guestPageBlocks';
import { parseMiniMarkdown, type MdNode } from '@/features/public-guides/miniMarkdown';
import {
  fileSizeLabel,
  formatDayMonYear,
  formatDmy,
  guestLook,
  phpPublic,
  sectionOn,
  type GuestLook,
  type PublicAttachment,
  type PublicGuestPortal,
  type PublicTransferBrand,
  type PublicTransferView,
  type PublicWelcomeMeeting,
} from './publicTransfer.api';
import {
  BLOCK_KINDS,
  blocksFor,
  isTransferLink,
  pillsForRegion,
  safeRichHtml,
  type ContactDesk,
  type Pill,
  type PillItem,
  type TransferBlock,
} from './transferLayout';
import './public-transfer.css';

// ── The company's own look (shared/company.ts under the Guest Portal's Look) ──

/**
 * The company's logo (the Guest Portal's, else the build's
 * `VITE_COMPANY_LOGO_URL`), else the logo the server resolved. Only for the
 * company's own logo: agents' logos and their co-brand (second) logo are
 * agent data, never replaced.
 */
export function companyLogo(look: GuestLook, serverUrl: string | null | undefined): string | null {
  return look.logoUrl || serverUrl || null;
}

const isDefaultAccent = (accent: string) => accent.toLowerCase() === COMPANY_DEFAULTS.accent.toLowerCase();

/**
 * The company's own colour on any brand (links, hover shadows): only a
 * company on another accent sets it. The stylesheet's literals ARE the Asian
 * Trails orange, so the default adds nothing to the DOM and keeps the plain
 * rgba() shadows the page always had.
 */
function companyOwnVars(accent: string): Record<string, string> {
  return isDefaultAccent(accent)
    ? {}
    : {
        '--ptx-co-accent': accent,
        '--ptx-co-shadow-10': `color-mix(in srgb, ${accent} 10%, transparent)`,
        '--ptx-co-shadow-20': `color-mix(in srgb, ${accent} 20%, transparent)`,
      };
}

/**
 * The company brand's own colours (the notice and error cards, and the
 * default-brand page when the Guest Portal sets an accent). Undefined on the
 * default accent, so those render as before.
 */
export function companyAccentVars(accent: string): CSSProperties | undefined {
  return isDefaultAccent(accent)
    ? undefined
    : ({
        ...companyOwnVars(accent),
        '--ptx-accent': accent,
        '--ptx-th-color': accent,
        '--ptx-accent-dark': `color-mix(in srgb, ${accent} 80%, #000)`,
        '--ptx-accent-bg': `color-mix(in srgb, ${accent} 2%, #fff)`,
        '--ptx-accent-border': `color-mix(in srgb, ${accent} 9%, #fff)`,
        '--ptx-accent-border-light': `color-mix(in srgb, ${accent} 17%, #fff)`,
        '--ptx-contact-bg': `color-mix(in srgb, ${accent} 2%, #fff)`,
      } as CSSProperties);
}

// ── The page ──────────────────────────────────────────────────────────────

/** What the editor's frame adds: which block is selected, and what a click does. */
export interface EditingState {
  selectedId: string | null;
  onSelect: (id: string) => void;
}

interface PageContext {
  view: PublicTransferView;
  brand: PublicTransferBrand;
  token: string;
  look: GuestLook;
  portal: PublicGuestPortal | null | undefined;
  editing: EditingState | null;
}

const Ctx = createContext<PageContext | null>(null);
const usePage = () => useContext(Ctx)!;

/** renderTransferPage (index.php:529-966): the found page, from its blocks. */
export function TransferPageBody({
  view,
  brand,
  token,
  editing = null,
}: {
  view: PublicTransferView;
  brand: PublicTransferBrand;
  token: string;
  editing?: EditingState | null;
}) {
  const portal = view.portal;
  const look = guestLook(portal);
  const vars = {
    '--ptx-accent': brand.accent,
    '--ptx-accent-dark': brand.accentDark,
    '--ptx-accent-bg': brand.accentBg,
    '--ptx-accent-border': brand.accentBorder,
    '--ptx-accent-border-light': brand.accentBorderLight,
    '--ptx-contact-bg': brand.contactBg,
    '--ptx-th-color': brand.thColor,
    // Only a company on another accent sets these (the CSS defaults are AT orange).
    ...companyOwnVars(look.accent),
    // The Guest Portal's accent paints the company's own brand; an agent's
    // look keeps the agent's colours.
    ...(brand.type === 'default' && portal?.look?.accent ? companyAccentVars(look.accent) : {}),
  } as CSSProperties;
  const blocks = blocksFor(view);

  return (
    <Ctx.Provider value={{ view, brand, token, look, portal, editing }}>
      <div className={editing ? 'ptx ptx--editing' : 'ptx'} style={vars} data-brand={brand.type}>
        <div className="ptx-container">
          {blocks.map((b) => (
            <BlockFrame key={b.id} block={b} />
          ))}

          <PrivacyNotice portal={portal} />

          {/* Footer (index.php:950-954) */}
          {brand.type === 'default' ? (
            <div className="ptx-footer-brand">
              © {new Date().getFullYear()} {brand.name} ·{' '}
              <a href="/guide/">Travel Guide</a>
              <br />
              created with{' '}
              <a href="https://amassa.ai" target="_blank" rel="noopener">
                amassa.ai
              </a>
            </div>
          ) : (
            <div className="ptx-footer-brand">
              · created with{' '}
              <a href="https://amassa.ai" target="_blank" rel="noopener">
                amassa.ai
              </a>{' '}
              ·
            </div>
          )}
        </div>
      </div>
    </Ctx.Provider>
  );
}

/** The tenant's privacy (PDPA) notice: only when the Guest Portal wrote one and left it on. */
export function PrivacyNotice({ portal }: { portal: PublicGuestPortal | null | undefined }) {
  if (!sectionOn(portal, 'privacy') || !portal?.privacyNotice) return null;
  return (
    <div className="ptx-privacy" role="note">
      <div className="ptx-privacy-title">Your privacy</div>
      <p>{portal.privacyNotice}</p>
    </div>
  );
}

/**
 * One block. On the guest page: the block itself, or nothing when hidden or
 * when it has nothing to show. In the editor's frame: a clickable frame
 * around it, with its name, the selection and a placeholder for a block that
 * has nothing to show or is hidden.
 */
function BlockFrame({ block }: { block: TransferBlock }) {
  const { editing } = usePage();
  if (!editing) return block.visible ? <BlockView block={block} /> : null;
  const kind = BLOCK_KINDS.find((k) => k.type === block.type);
  const label = kind?.label ?? block.type;
  const selected = editing.selectedId === block.id;
  const cls = ['tpv-block', selected ? 'is-selected' : '', block.visible ? '' : 'is-hidden'].filter(Boolean).join(' ');
  return (
    <div
      className={cls}
      data-tp-id={block.id}
      data-tp-type={block.type}
      role="button"
      tabIndex={0}
      aria-label={`${label}${block.visible ? '' : ', hidden'}${selected ? ', selected' : ''}`}
      aria-pressed={selected}
      onClick={(e) => {
        e.preventDefault();
        editing.onSelect(block.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          editing.onSelect(block.id);
        }
      }}
    >
      <span className="tpv-block__tag" aria-hidden>
        {label}
        {block.visible ? '' : ' · hidden'}
      </span>
      {/* A block that renders nothing (hidden, or nothing to show for this booking) leaves the body empty; CSS prints why. */}
      <div className="tpv-block__body" data-empty={block.visible ? `${label}: nothing to show for this booking` : `${label} is hidden`}>
        {block.visible ? <BlockView block={block} /> : null}
      </div>
    </div>
  );
}

/** The block's own component; a block with nothing to show for this booking renders null. */
export function BlockView({ block }: { block: TransferBlock }) {
  switch (block.type) {
    case 'header':
      return <Header />;
    case 'logo':
      return <Logo props={block.props} />;
    case 'hero':
      return <Hero props={block.props} />;
    case 'services':
      return <Services props={block.props} />;
    case 'meetings':
      return <Meetings props={block.props} />;
    case 'files':
      return <Files props={block.props} />;
    case 'pills':
      return <Pills props={block.props} />;
    case 'contact':
      return <Contact props={block.props} />;
    case 'richText':
      return <RichText props={block.props} />;
    case 'image':
      return <Image props={block.props} />;
    case 'button':
      return <ButtonLink props={block.props} />;
    case 'divider':
      return <Divider props={block.props} />;
    case 'faq':
      return <Faq props={block.props} />;
    default:
      return null;
  }
}

// ── Header (index.php:702-712) ────────────────────────────────────────────

function Header() {
  const { view, brand, look } = usePage();
  const isKuoni = brand.type === 'kuoni';
  // The default brand's logo is the company's own; an agent's look keeps its logo.
  const mainLogo = brand.type === 'default' ? companyLogo(look, brand.logoUrl) : brand.logoUrl;
  if (isKuoni) {
    return (
      <div className="ptx-brand-header ptx-brand-header--kuoni">
        {brand.logoUrl && <img src={brand.logoUrl} alt={brand.name} className="ptx-logo--kuoni-main" />}
        {brand.secondLogoUrl && <img src={brand.secondLogoUrl} alt={view.companyName} className="ptx-logo--at" />}
      </div>
    );
  }
  if (!mainLogo) return null;
  return (
    <div className={brand.secondLogoUrl ? 'ptx-brand-header ptx-brand-header--cobrand' : 'ptx-brand-header'}>
      <img src={mainLogo} alt={brand.name} />
      {/* The agent's co-brand logo (agent window, 2026-10-02): any agent may switch it on, not only Kuoni. */}
      {brand.secondLogoUrl && <img src={brand.secondLogoUrl} alt="" className="ptx-logo--at" />}
    </div>
  );
}

/** One logo on its own: the company's, the agent's (only when its look is on), or any image. */
function Logo({ props }: { props: TransferBlock<'logo'>['props'] }) {
  const { view, brand, look } = usePage();
  const src =
    props.source === 'company'
      ? companyLogo(look, view.companyLogoUrl)
      : props.source === 'agent'
        ? brand.type === 'default'
          ? null
          : brand.logoUrl
        : props.url && !/^javascript:/i.test(props.url) && (/^https?:\/\//i.test(props.url) || /^\/(?!\/)/.test(props.url))
          ? props.url
          : null;
  if (!src) return null;
  return (
    <div className={`ptx-logo-block ptx-logo-block--${props.align}`}>
      <img src={src} alt={props.alt ?? (props.source === 'agent' ? brand.name : view.companyName)} style={{ height: props.height }} />
    </div>
  );
}

// ── Hero (index.php:714-718) ──────────────────────────────────────────────

function Hero({ props }: { props: TransferBlock<'hero'>['props'] }) {
  const { view, brand, token } = usePage();
  return (
    <div className={brand.type === 'whitelabel' ? 'ptx-hero ptx-hero--subtle' : 'ptx-hero'}>
      <h1>{props.title}</h1>
      {props.subtitle && <p className="ptx-hero-sub">{props.subtitle}</p>}
      {props.showRef && <div className="ptx-ref">REF: {view.ref ?? token}</div>}
    </div>
  );
}

// ── Services (index.php:721-791) ─────────────────────────────────────────

const COLUMN_HEAD: Record<TransferBlock<'services'>['props']['columns'][number], string> = {
  date: 'Date',
  pax: 'Pax',
  time: 'Time',
  pickup: 'Pick-up',
  dropoff: 'Drop-off',
  flight: 'Flight',
  product: 'Product',
};

function Services({ props }: { props: TransferBlock<'services'>['props'] }) {
  const { view } = usePage();
  const services = view.services ?? [];
  const cols = props.columns;
  const has = (c: (typeof cols)[number]) => cols.includes(c);
  return (
    <div className="ptx-content">
      {/* index.php:721-723 */}
      {props.showPeriodNote && services.length > 0 && view.period && (
        <div className="ptx-sched-note">
          Transfers &amp; services shown below are for the period{' '}
          <strong>
            {formatDayMonYear(view.period.from)} to {formatDayMonYear(view.period.to)}
          </strong>
          . Services for later dates will only appear here <strong>2 days prior the service date</strong>.
        </div>
      )}

      {/* Desktop table (index.php:732-762) */}
      <div className="ptx-tbl-wrap">
        <table className="ptx-tbl">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c}>{COLUMN_HEAD[c]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {services.map((s, i) => (
              <tr key={i}>
                {has('date') && <td className="ptx-td-date">{formatDmy(s.date)}</td>}
                {has('pax') && <td className="ptx-td-pax">{s.pax ?? '-'}</td>}
                {has('time') && <td className="ptx-td-time">{s.time || '-'}</td>}
                {has('pickup') && <td className="ptx-td-loc">{s.pickup ?? '-'}</td>}
                {has('dropoff') && <td className="ptx-td-loc">{s.dropoff ?? '-'}</td>}
                {has('flight') && <td>{s.flight ? <span className="ptx-td-flight">{s.flight}</span> : '-'}</td>}
                {has('product') && <td className="ptx-td-product">{s.product ?? '-'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards (index.php:764-791) */}
      <div className="ptx-m-cards">
        {services.map((s, i) => (
          <div className="ptx-m-card" key={i}>
            <div className="ptx-m-card-hdr">
              <span className="ptx-m-card-date">
                {has('date') ? formatDmy(s.date) : ''}
                {has('time') && s.time ? `${has('date') ? ' @ ' : ''}${s.time}` : ''}
              </span>
            </div>
            {has('pax') && <MRow label="Pax" value={s.pax ?? '-'} />}
            {has('pickup') && <MRow label="Pick-up" value={s.pickup ?? '-'} />}
            {has('dropoff') && <MRow label="Drop-off" value={s.dropoff ?? '-'} />}
            {has('flight') && s.flight && <MRow label="Flight" value={s.flight} />}
            {has('product') && <MRow label="Product" value={s.product ?? '-'} product />}
          </div>
        ))}
      </div>
    </div>
  );
}

function MRow({ label, value, product = false }: { label: string; value: string | number; product?: boolean }) {
  return (
    <div className={product ? 'ptx-m-row ptx-m-product' : 'ptx-m-row'}>
      <span className="ptx-m-lbl">{label}</span>
      <span className="ptx-m-val">{value}</span>
    </div>
  );
}

// ── Welcome meetings (index.php:796-838) ─────────────────────────────────

function Meetings({ props }: { props: TransferBlock<'meetings'>['props'] }) {
  const { view } = usePage();
  const meetings = view.welcomeMeetings ?? [];
  if (meetings.length === 0) return null;
  return <WelcomeMeetings meetings={meetings} title={props.title} subtitle={props.subtitle} />;
}

/** Shown for every brand type (showWM is never false). */
export function WelcomeMeetings({ meetings, title, subtitle }: { meetings: PublicWelcomeMeeting[]; title: string; subtitle?: string }) {
  return (
    <div className="ptx-wm-section">
      <div className="ptx-wm-title">{title}</div>
      {subtitle && <div className="ptx-wm-sub">{subtitle}</div>}
      <div className="ptx-wm-cards">
        {meetings.map((wm, i) => (
          <div className="ptx-wm-card" key={i}>
            <div className="ptx-wm-photo">
              {wm.photoUrl ? (
                <img src={wm.photoUrl} alt={wm.rep} width={52} height={52} />
              ) : (
                <svg width="52" height="52" viewBox="0 0 52 52" fill="none" aria-hidden="true">
                  <circle cx="26" cy="26" r="26" fill="#E8E8E8" />
                  <circle cx="26" cy="20" r="9" fill="#BDBDBD" />
                  <ellipse cx="26" cy="42" rx="16" ry="10" fill="#BDBDBD" />
                </svg>
              )}
            </div>
            <div className="ptx-wm-info">
              <div className="ptx-wm-date-title">
                {wm.date ? formatDmy(wm.date) : ''}
                {wm.time ? ` @ ${wm.time}` : ''}
              </div>
              <div className="ptx-wm-rep-name">{wm.rep}</div>
              {wm.location && (
                <div className="ptx-wm-meta">
                  <span className="ptx-wm-lbl">Location</span> {wm.location}
                </div>
              )}
              {wm.phone && (
                <div className="ptx-wm-meta">
                  <span className="ptx-wm-lbl">Phone</span>{' '}
                  <a href={`tel:${wm.phone}`} className="ptx-wm-phone">
                    {wm.phone}
                  </a>
                  {wm.whatsapp && (
                    <a href={`https://wa.me/${wm.whatsapp}`} className="ptx-wm-wa">
                      WhatsApp
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Documents (index.php:890-929) ────────────────────────────────────────

function Files({ props }: { props: TransferBlock<'files'>['props'] }) {
  const { view, look } = usePage();
  const attachments = (view.attachments ?? []).filter((a) => a.url);
  if (attachments.length === 0) return null;
  return <Documents attachments={attachments} accent={look.accent} title={props.title} />;
}

export function Documents({ attachments, accent, title }: { attachments: PublicAttachment[]; accent: string; title: string }) {
  return (
    <div className="ptx-docs">
      <h2 className="ptx-docs-title">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" aria-hidden="true">
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
        </svg>
        {title}
      </h2>
      <div className="ptx-docs-list">
        {attachments.map((att, i) => {
          const type = att.type ?? '';
          const color = type.includes('pdf') ? '#dc2626' : type.startsWith('image') ? '#3b82f6' : '#64748b';
          return (
            <a key={i} href={att.url ?? undefined} target="_blank" rel="noopener noreferrer" className="ptx-doc">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" aria-hidden="true">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
              <div className="ptx-doc-text">
                <div className="ptx-doc-name">{att.name}</div>
                <div className="ptx-doc-size">{fileSizeLabel(att.size)}</div>
              </div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" aria-hidden="true">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </a>
          );
        })}
      </div>
    </div>
  );
}

// ── Links (index.php:840-888, the Explore block) ─────────────────────────

/**
 * Rows of pills. The server filled the pills of every row that names a source
 * (or the page did, from its own lists); the driver, feedback and plain links
 * are the page's own. A row with nothing to show is left out, as the fixed
 * rows always were; an agent working in one country sees that country's pills.
 */
function Pills({ props }: { props: TransferBlock<'pills'>['props'] }) {
  const { brand, token } = usePage();
  const rows = props.rows
    .map((row) => ({ row, pills: row.items.flatMap((item) => itemPills(item, brand.region, token)) }))
    .filter((r) => r.pills.length > 0);
  if (rows.length === 0) return null;
  return (
    <>
      <div className="ptx-explore-divider" />
      <div className="ptx-explore">
        {props.heading && <div className="ptx-explore-heading">{props.heading}</div>}
        {rows.map(({ row, pills }) => (
          <div className="ptx-explore-row" key={row.id}>
            {row.label && <div className="ptx-explore-label">{row.label}</div>}
            <div className="ptx-explore-pills">
              {pills.map((p, i) => (
                <Fragment key={i}>{p}</Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function itemPills(item: PillItem, region: string | null | undefined, token: string): ReactNode[] {
  switch (item.kind) {
    case 'driver':
      return [
        <a href="/pickup" className="ptx-pill-driver">
          <PinIcon />
          {item.label ?? 'Where to Find Your Driver'}
        </a>,
      ];
    case 'feedback':
      // index.php:877-886: the URL's ref rides in the path so the form prefills.
      return [
        <a href={`${phpPublic()}/feedback/${encodeURIComponent(token)}`} className="ptx-pill-driver">
          <StarIcon />
          {item.label ?? 'Rate Your Trip'}
        </a>,
      ];
    case 'link': {
      if (!item.href || !item.label || !isTransferLink(item.href)) return [];
      const external = /^https:/i.test(item.href);
      return [
        <a
          href={item.href}
          className={item.style === 'accent' ? 'ptx-pill-driver' : undefined}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener' : undefined}
        >
          {item.label}
        </a>,
      ];
    }
    default:
      return pillsForRegion(item.pills ?? [], region).map((p: Pill) => <a href={p.href}>{p.label}</a>);
  }
}

// ── Contact (index.php:931-949) ──────────────────────────────────────────

function Contact({ props }: { props: TransferBlock<'contact'>['props'] }) {
  const { brand, look } = usePage();
  // The company's two desks (the Look over the company profile), narrowed to the agent's country, or the block's own.
  const desks: ContactDesk[] =
    props.desks === 'auto'
      ? [
          { id: 'my', label: '🇲🇾 Malaysia', phone: look.hotlineMy, email: look.csEmailMy, country: 'my' as const },
          { id: 'sg', label: '🇸🇬 Singapore', phone: look.hotlineSg, email: look.csEmailSg, country: 'sg' as const },
        ].filter((d) => !brand.region || d.country === brand.region)
      : props.desks.filter((d) => !d.country || !brand.region || d.country === brand.region);
  const showDesks = props.showDesks && desks.length > 0;
  if (!showDesks && !props.showSignOff) return null;
  const signOff = props.showSignOff && (
    <div className="ptx-sign-off">
      <div className="ptx-happy-trails">{look.signOff}</div>
      <div className="ptx-sign-team">
        {props.team && (
          <>
            {props.team}
            <br />
          </>
        )}
        <strong>{brand.signature}</strong>
      </div>
    </div>
  );
  if (!showDesks) {
    // The sign-off stays when the desk's buttons are switched off.
    return <div className="ptx-contact-box">{signOff}</div>;
  }
  return (
    <div className="ptx-contact-box">
      {props.intro && <p className="ptx-contact-intro">{props.intro}</p>}
      <div className={desks.length > 1 ? 'ptx-contact-grid' : 'ptx-contact-grid ptx-contact-grid--one'}>
        {desks.map((d) => (
          <div className="ptx-contact-col" key={d.id}>
            <h3>{d.label}</h3>
            {d.phone && (
              <a href={`https://wa.me/${waDigits(d.phone)}`} className="ptx-contact-link">
                <span>Phone / WhatsApp</span>
                {d.phone}
              </a>
            )}
            {d.email && (
              <a href={`mailto:${d.email}`} className="ptx-contact-link">
                <span>Email</span>
                {d.email}
              </a>
            )}
          </div>
        ))}
      </div>
      {signOff}
    </div>
  );
}

// ── The free blocks (2026-10-29) ─────────────────────────────────────────

function RichText({ props }: { props: TransferBlock<'richText'>['props'] }) {
  const html = safeRichHtml(props.html);
  if (!html) return null;
  // The API rebuilt it from an allow-list on save and on read; DOMPurify ran once more just above.
  return <div className="ptx-rich" dangerouslySetInnerHTML={{ __html: html }} />;
}

function Image({ props }: { props: TransferBlock<'image'>['props'] }) {
  if (!props.url || !isGuestImage(props.url)) return null;
  return (
    <figure className={props.width === 'full' ? 'ptx-figure ptx-figure--full' : 'ptx-figure'}>
      <img src={props.url} alt={props.alt ?? props.caption ?? ''} loading="lazy" />
      {props.caption && <figcaption>{props.caption}</figcaption>}
    </figure>
  );
}

function ButtonLink({ props }: { props: TransferBlock<'button'>['props'] }) {
  if (!props.label || !props.href || !isTransferLink(props.href)) return null;
  const external = /^https:/i.test(props.href);
  return (
    <div className={`ptx-cta ptx-cta--${props.align}`}>
      <a
        className={`ptx-btn ptx-btn--${props.style}`}
        href={props.href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener' : undefined}
      >
        {props.label}
      </a>
    </div>
  );
}

function Divider({ props }: { props: TransferBlock<'divider'>['props'] }) {
  return <div className={`ptx-divider ptx-divider--${props.space}`} aria-hidden />;
}

function Faq({ props }: { props: TransferBlock<'faq'>['props'] }) {
  const items = props.items.filter((it) => it.q.trim() && it.a.trim());
  if (items.length === 0) return null;
  return (
    <div className="ptx-faq">
      {props.title && <h2 className="ptx-faq-title">{props.title}</h2>}
      <div className="ptx-faq-list">
        {items.map((it, i) => (
          <details className="ptx-faq-item" key={i}>
            <summary>{it.q}</summary>
            <div className="ptx-faq-a">
              <MiniMarkdown text={it.a} />
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

/** The guide pages' mini markdown (bold, italic, safe links, line breaks), as elements. */
function MiniMarkdown({ text }: { text: string }) {
  return <>{renderNodes(parseMiniMarkdown(text))}</>;
}

function renderNodes(nodes: MdNode[]): ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.t) {
      case 'text':
        return <Fragment key={i}>{n.v}</Fragment>;
      case 'br':
        return <br key={i} />;
      case 'strong':
        return <strong key={i}>{renderNodes(n.c)}</strong>;
      case 'em':
        return <em key={i}>{renderNodes(n.c)}</em>;
      case 'link':
        return (
          <a key={i} href={n.href} target="_blank" rel="noopener">
            {renderNodes(n.c)}
          </a>
        );
    }
  });
}

// ── Icons ─────────────────────────────────────────────────────────────────

export function PinIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export function StarIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11.48 3.5a.56.56 0 0 1 1.04 0l2.13 4.32a.56.56 0 0 0 .42.3l4.77.7c.46.06.64.63.31.95l-3.45 3.36a.56.56 0 0 0-.16.5l.81 4.75a.56.56 0 0 1-.81.59l-4.27-2.24a.56.56 0 0 0-.52 0l-4.27 2.24a.56.56 0 0 1-.81-.59l.81-4.75a.56.56 0 0 0-.16-.5L3.85 9.77a.56.56 0 0 1 .31-.95l4.77-.7a.56.56 0 0 0 .42-.3Z" />
    </svg>
  );
}
